import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from 'Components/Atoms/TextField';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import { toEuropeanDate } from 'utils/helper';
import { ExclusiveOffer, LIVE_OFFER, VisitWish } from './visitWishApi';

export const DEFAULT_OFFER_MINUTES = 120;

export const OFFER_LABELS: Record<ExclusiveOffer['status'], string> = {
  holding: 'Table held, offer being sent',
  offered: 'Offer sent',
  accepting: 'Guest is completing the booking',
  booked: 'Booked through the offer',
  expired: 'Offer expired',
  released: 'Offer withdrawn',
  failed: 'Offer could not be made',
};

export const clock = (time: number, language: string) =>
  new Intl.DateTimeFormat(language === 'de' ? 'de-DE' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
    timeZoneName: 'short',
  }).format(new Date(time));

export const liveOffer = (wish: VisitWish) => wish.offers?.find((offer) => LIVE_OFFER.includes(offer.status)) ?? null;

/** What the team needs to know about a wish's exclusivity: marking, the held table, the deadline. */
export const ExclusiveCell: React.FC<{ wish: VisitWish }> = ({ wish }) => {
  const { t, i18n } = useTranslation('reservations');
  const offer = liveOffer(wish) ?? wish.offers?.[0] ?? null;
  if (!wish.exclusive && !offer) {
    return (
      <Typography variant="text-3" color="subdued" block>
        –
      </Typography>
    );
  }
  const deadline = offer?.acceptUntil ?? offer?.expiresAt ?? null;
  return (
    <Typography variant="text-3" color="subdued" block>
      {wish.exclusive ? t('Exclusive, {{minutes}} min', { minutes: wish.exclusive.durationMinutes }) : null}
      {offer ? (
        <>
          <br />
          {t(OFFER_LABELS[offer.status])}
          {LIVE_OFFER.includes(offer.status)
            ? ` · ${toEuropeanDate(offer.slot.date)} ${offer.slot.time} · ${t('Table')} ${offer.tables.join(', ')}`
            : ''}
          {LIVE_OFFER.includes(offer.status) && deadline
            ? ` · ${t('until {{time}}', { time: clock(deadline, i18n.language) })}`
            : ''}
        </>
      ) : null}
    </Typography>
  );
};

/** Asks for the offer duration (Q9: two hours unless the team chooses otherwise). */
export const ExclusiveModal: React.FC<{
  wish: VisitWish | null;
  onClose: () => void;
  onConfirm: (minutes: number) => Promise<void>;
}> = ({ wish, onClose, onConfirm }) => {
  const { t } = useTranslation('reservations');
  const [minutes, setMinutes] = useState(String(DEFAULT_OFFER_MINUTES));
  const [saving, setSaving] = useState(false);
  const valid = minutes.trim() !== '' && Number.isInteger(Number(minutes)) && Number(minutes) >= 15 && Number(minutes) <= 24 * 60;

  return (
    <Modal
      open={!!wish}
      onClose={onClose}
      maxWidth="sm"
      title={t('Hold a table exclusively')}
      actions={[
        { id: 'cancel', label: 'Cancel', translation: 'reservations', onClick: onClose },
        {
          id: 'confirm',
          label: 'Mark exclusive',
          translation: 'reservations',
          variant: 'primary',
          disabled: !valid || saving,
          loading: saving,
          onClick: async () => {
            setSaving(true);
            try {
              await onConfirm(Number(minutes));
            } finally {
              setSaving(false);
            }
          },
        },
      ]}
    >
      <Typography variant="text-3" translation="reservations" block>
        As soon as a suitable table becomes free, it is held for this guest only and offered until the deadline. Without a booking it is released again and the wish stays active.
      </Typography>
      <TextField
        type="number"
        label="Offer duration in minutes"
        labelTranslation="reservations"
        value={minutes}
        onChange={(event) => setMinutes(event.target.value)}
        disabled={saving}
        inputProps={{ min: 15, max: 1440, step: 1, inputMode: 'numeric' }}
        error={minutes !== '' && !valid}
        helperText={t('Choose between 15 and 1440 minutes.')}
        helperTextTranslation={null}
        style={{ marginTop: 16 }}
      />
    </Modal>
  );
};
